import { oc } from "@orpc/contract";
import { z } from "zod";
import { spaRequestsContracts } from "./pricing/spa-requests.contract";
import { as400LoadContracts } from "./pricing/as400-load.contract";
import { spasContracts } from "./pricing/spas.contract";
import { masterlistContracts } from "./pricing/masterlist.contract";
import { agreementExtractContracts } from "./pricing/agreement-extract.contract";
import { uploadRulesContracts } from "./pricing/upload-rules.contract";
import { renewalRulesContracts } from "./pricing/renewal-rules.contract";
import { uploadLayoutsContracts } from "./pricing/upload-layouts.contract";
import { vendorPortalFilesContracts } from "./pricing/vendor-portal-files.contract";
import { vendorPortalsContracts } from "./pricing/vendor-portals.contract";
import { hubbellFeedbackContracts } from "./pricing/hubbell-feedback.contract";
import { massRenewalContracts } from "./pricing/mass-renewal.contract";

const GetSpaWorkflowRunInputSchema = z.object({
  spaNumber: z.string().min(1, "SPA number is required"),
});

/** A parsed vendor SPA line (mirrors the fetch_vendor_spa output). */
const SpaWorkflowLineSchema = z.object({
  quoteNumber: z.string(),
  productNumber: z.string(),
  price: z.string().nullable(),
  uom: z.string().nullable(),
  per: z.string().nullable(),
  description: z.string().nullable(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
});

/** A computed price change (mirrors the calculate_spa_price_changes output). */
const SpaWorkflowPriceChangeSchema = z.object({
  productNumber: z.string(),
  quoteNumber: z.string(),
  description: z.string().nullable(),
  newPrice: z.string().nullable(),
  ovCostNet: z.string().nullable(),
  loadsBuyers: z.string().nullable(),
  priceDelta: z.string().nullable(),
  percentChange: z.string().nullable(),
  buyersDelta: z.string().nullable(),
  buyersPercentChange: z.string().nullable(),
  status: z.string(),
});

const SpaWorkflowRunOutputSchema = z.object({
  run: z
    .object({
      workflowRunId: z.string(),
      workflowId: z.string(),
      workflowDefinitionId: z.string(),
      status: z.string(),
      startedAt: z.string().nullable(),
    })
    .nullable(),
  vendorSpa: z
    .object({
      status: z.string(),
      sourceFilePath: z.string().nullable(),
      sheetName: z.string().nullable(),
      validFrom: z.string().nullable(),
      validTo: z.string().nullable(),
      lineCount: z.number().nullable(),
      spaLines: z.array(SpaWorkflowLineSchema),
    })
    .nullable(),
  currentLoads: z
    .object({
      status: z.string(),
      quoteNumber: z.string().nullable(),
      sourceFilePath: z.string().nullable(),
      scanned: z.number().nullable(),
      lineCount: z.number().nullable(),
    })
    .nullable(),
  priceChanges: z
    .object({
      status: z.string(),
      changeCount: z.number().nullable(),
      priceChanges: z.array(SpaWorkflowPriceChangeSchema),
    })
    .nullable(),
});

/**
 * Pricing endpoints — SPA request CRUD (Module 1) + workflow triggers.
 */
export const pricingContracts = {
  as400Load: as400LoadContracts,
  spaRequests: spaRequestsContracts,
  spas: spasContracts,
  masterlist: masterlistContracts,
  agreementExtract: agreementExtractContracts,
  uploadRules: uploadRulesContracts,
  renewalRules: renewalRulesContracts,
  uploadLayouts: uploadLayoutsContracts,
  vendorPortalFiles: vendorPortalFilesContracts,
  vendorPortals: vendorPortalsContracts,
  hubbellFeedback: hubbellFeedbackContracts,
  massRenewal: massRenewalContracts,
  /**
   * Read the latest SPA Change Request workflow run for a SPA number and the
   * outputs of its key steps (vendor SPA, AS400 current loads, price changes).
   * Powers the live workflow stepper in the SPA detail view.
   */
  getSpaWorkflowRun: oc
    .input(GetSpaWorkflowRunInputSchema)
    .output(SpaWorkflowRunOutputSchema)
    .route({
      method: "GET",
      summary: "Get SPA change request workflow progress",
      description:
        "Returns the latest SPA change request workflow run for a SPA number, with its vendor SPA lines, current ERP price loads, and computed price changes.",
    }),
} as const;

export type SpaWorkflowRunOutput = z.infer<typeof SpaWorkflowRunOutputSchema>;
export type SpaWorkflowLine = z.infer<typeof SpaWorkflowLineSchema>;
export type SpaWorkflowPriceChange = z.infer<
  typeof SpaWorkflowPriceChangeSchema
>;
